import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box,
  HStack,
  Stack,
  Button,
  useToast 
} from '@chakra-ui/react';
import { getVideoData, updateVideoData } from '../../../services/Videos';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import FormLoader from '../../../CommonComponents/FormLoader';
import { useSelector } from 'react-redux';
import { handleApiError, showSuccessToast } from '../../../util/errorHandler';

const Section5 = () => {

  const { alias } = useParams();
  const [quickSave, setQuickSave] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});
  const previewEnabled = useSelector(state => state.preview.isEnabled);
  const toast = useToast();

  // Split data for two-column layout
  const splitFormData = (data = {}) => {
    const leftFields = ['_id', 'agraphy_section_or_sub_section', 'agraphy_specific_section', 'type_of_creator', 'type_of_joint_creator', 'country_or_state_of_artwork_creation', 'location_masterlist', 'list_of_artists', 'list_of_artists_alias', 'insight_number', 'insight_text_note1', 'insight_text_note2', 'insight_text_hyperlink', 'essential_reading', 'suggested_reading', 'highlight', 'accession_number', 'personality_or_film_or_artwork_on_front_cover', 'personality_or_film_or_artwork_on_front_cover_alias', 'publisher', 'publisher_alias', 'object_alias', 'quotations_and_other_text', 'theme', 'contributors1', 'contributors1_type', 'contributors1_alias', 'contributors2', 'contributors2_type', 'contributors2_alias', 'language_data', 'citation_quotation', 'copyright_of_image', 'age'];
    const rightFields = ['set_of', 'copyright', 'facial_recognition_derived_title_of_artwork', 'is_source', 'search_text', 'extended_tags', 'rec_aliases'];
    
    const leftData = {};
    const rightData = {};
    
    leftFields.forEach(field => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      }
    });
    
    rightFields.forEach(field => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      }
    });
    
    return { leftData, rightData };
  };
  
  // Combine data from two forms
  const combineFormData = (leftData, rightData) => {
    return { ...leftData, ...rightData };
  };

  // Form change handlers
  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  // Left column schema
  const leftColumnSchema = {
    "title": "",
    "type": "object",
    "properties": {
      "_id": { "type": "string" },
      "agraphy_section_or_sub_section": {
        "type": "string",
        "title": "Agraphy Section or Sub-section"
      },
      "agraphy_specific_section": {
        "type": "string",
        "title": "Agraphy Specific Section"
      },
      "type_of_joint_creator": {
        "type": "string",
        "title": "Type of Joint Creator"
      },
      "type_of_creator": {
        "type": "string",
        "title": "Type of Creator"
      },
      "country_or_state_of_artwork_creation": {
        "type": "string",
        "title": "Country or State of Artwork Creation"
      },
      "location_masterlist": {
        "type": "string",
        "title": "Location Masterlist"
      },
      "list_of_artists": {
        "type": "string",
        "title": "List of Artists"
      },
      "list_of_artists_alias": {
        "type": "string",
        "title": "List of Artists Alias"
      },
      "insight_number": {
        "type": "string",
        "title": "Insight Number"
      },
      "insight_text_note1": {
        "type": "string",
        "title": "Insight Text Note 1"
      },
      "insight_text_note2": {
        "type": "string",
        "title": "Insight Text Note 2"
      },
      "insight_text_hyperlink": {
        "type": "string",
        "title": "Insight Text Hyperlink"
      },
      "essential_reading": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Essential Reading"
      },
      "suggested_reading": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Suggested Reading"
      },
      "highlight": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Highlight"
      },
      "accession_number": {
        "type": "string",
        "title": "Accession Number"
      },
      "personality_or_film_or_artwork_on_front_cover": {
        "type": "string",
        "title": "Personality, Film, or Artwork on Front Cover"
      },
      "personality_or_film_or_artwork_on_front_cover_alias": {
        "type": "string",
        "title": "Personality, Film, or Artwork on Front Cover Alias"
      },
      "publisher": {
        "type": "string",
        "title": "Publisher"
      },
      "publisher_alias": {
        "type": "string",
        "title": "Publisher Alias"
      },
      "object_alias": {
        "type": "string",
        "title": "Object Alias"
      },
      "quotations_and_other_text": {
        "type": "string",
        "title": "Quotations and Other Text"
      },
      "theme": {
        "type": "string",
        "title": "Theme"
      },
      "contributors1": {
        "type": "string",
        "title": "Contributors 1"
      },
      "contributors1_type": {
        "type": "string",
        "title": "Contributors 1 Type"
      },
      "contributors1_alias": {
        "type": "string",
        "title": "Contributors 1 Alias"
      },
      "contributors2": {
        "type": "string",
        "title": "Contributors 2"
      },
      "contributors2_type": {
        "type": "string",
        "title": "Contributors 2 Type"
      },
      "contributors2_alias": {
        "type": "string",
        "title": "Contributors 2 Alias"
      },
      "language_data": {
        "type": "string",
        "title": "Language Data"
      },
      "citation_quotation": {
        "type": "string",
        "title": "Citation Quotation"
      },
      "copyright_of_image": {
        "type": "string",
        "title": "Copyright of Image"
      },
      "age": {
        "type": "string",
        "title": "Age"
      }
    }
  };

  // Right column schema
  const rightColumnSchema = {
    "title": "",
    "type": "object",
    "properties": {
      "set_of": {
        "type": "string",
        "title": "Set Of"
      },
      "copyright": {
        "type": "string",
        "title": "Copyright"
      },
      "facial_recognition_derived_title_of_artwork": {
        "type": "string",
        "title": "Facial Recognition Derived Title of Artwork"
      },
      "is_source": {
        "type": "string",
        "title": "Is Source",
        "default": ""
      },
      "search_text": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Search Text"
      },
      "extended_tags": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Extended Tags"
      },
      "rec_aliases": {
        "type": "array",
        "items": {
          "type": "string"
        },
        "title": "Rec Aliases"
      }
    }
  };

  // Combined schema for preview mode
  const combinedSchema = {
  "title": "Quadrant-5 fields",
  "description": "",
  "type": "object",
  "properties": {
      "_id": { "type": "string" },
    "agraphy_section_or_sub_section": {
      "type": "string",
      "title": "Agraphy Section or Sub-section"
    },
    "agraphy_specific_section": {
      "type": "string",
      "title": "Agraphy Specific Section"
    },
    "type_of_joint_creator": {
      "type": "string",
      "title": "Type of Joint Creator"
    },
    "type_of_creator": {
      "type": "string",
      "title": "Type of Creator"
    },
    "country_or_state_of_artwork_creation": {
      "type": "string",
      "title": "Country or State of Artwork Creation"
    },
    "location_masterlist": {
      "type": "string",
      "title": "Location Masterlist"
    },
    "list_of_artists": {
      "type": "string",
      "title": "List of Artists"
    },
    "list_of_artists_alias": {
      "type": "string",
      "title": "List of Artists Alias"
    },
    "insight_number": {
      "type": "string",
      "title": "Insight Number"
    },
    "insight_text_note1": {
      "type": "string",
      "title": "Insight Text Note 1"
    },
    "insight_text_note2": {
      "type": "string",
      "title": "Insight Text Note 2"
    },
    "insight_text_hyperlink": {
      "type": "string",
      "title": "Insight Text Hyperlink"
    },
    "essential_reading": {
      "type": "array",
      "items": {
        "type": "string"
      },
      "title": "Essential Reading"
    },
    "suggested_reading": {
      "type": "array",
      "items": {
        "type": "string"
      },
      "title": "Suggested Reading"
    },
    "highlight": {
      "type": "array",
      "items": {
        "type": "string"
      },
      "title": "Highlight"
    },
    "accession_number": {
      "type": "string",
      "title": "Accession Number"
    },
    "personality_or_film_or_artwork_on_front_cover": {
      "type": "string",
      "title": "Personality, Film, or Artwork on Front Cover"
    },
    "personality_or_film_or_artwork_on_front_cover_alias": {
      "type": "string",
      "title": "Personality, Film, or Artwork on Front Cover Alias"
    },
    "publisher": {
      "type": "string",
      "title": "Publisher"
    },
    "publisher_alias": {
      "type": "string",
      "title": "Publisher Alias"
    },
    "object_alias": {
      "type": "string",
      "title": "Object Alias"
    },
    "quotations_and_other_text": {
      "type": "string",
      "title": "Quotations and Other Text"
    },
    "theme": {
      "type": "string",
      "title": "Theme"
    },
    "contributors1": {
      "type": "string",
      "title": "Contributors 1"
    },
    "contributors1_type": {
      "type": "string",
      "title": "Contributors 1 Type"
    },
    "contributors1_alias": {
      "type": "string",
      "title": "Contributors 1 Alias"
    },
    "contributors2": {
      "type": "string",
      "title": "Contributors 2"
    },
    "contributors2_type": {
      "type": "string",
      "title": "Contributors 2 Type"
    },
    "contributors2_alias": {
      "type": "string",
      "title": "Contributors 2 Alias"
    },
    "language_data": {
      "type": "string",
      "title": "Language Data"
    },
    "set_of": {
      "type": "string",
      "title": "Set Of"
    },
    "copyright": {
      "type": "string",
      "title": "Copyright"
    },
    "facial_recognition_derived_title_of_artwork": {
      "type": "string",
      "title": "Facial Recognition Derived Title of Artwork"
    },
    "is_source": {
      "type": "string",
      "title": "Is Source",
      "default": ""
    },
    "search_text": {
      "type": "array",
      "items": {
        "type": "string"
      },
      "title": "Search Text"
    },
    "extended_tags": {
      "type": "array",
      "items": {
        "type": "string"
      },
      "title": "Extended Tags"
    },
    "rec_aliases": {
      "type": "array",
      "items": {
        "type": "string"
      },
      "title": "Rec Aliases"
    },
    "citation_quotation": {
      "type": "string",
      "title": "Citation Quotation"
    },
    "copyright_of_image": {
      "type": "string",
      "title": "Copyright of Image"
    },
    "age": {
      "type": "string",
      "title": "Age"
    }
  }
  };

  // Left column UI schema
  const leftColumnUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "_id": { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    "highlight": {
      "ui:readonly": true,
      "ui:emptyValue": ""
    },
    "essential_reading": {
      "ui:emptyValue": '',
    },
    "suggested_reading": {
      "ui:emptyValue": '',
    },
    "agraphy_section_or_sub_section": { "ui:emptyValue": "" },
    "agraphy_specific_section": { "ui:emptyValue": "" },
    "type_of_joint_creator": { "ui:emptyValue": "" },
    "type_of_creator": { "ui:emptyValue": "" },
    "country_or_state_of_artwork_creation": { "ui:emptyValue": "" },
    "location_masterlist": { "ui:emptyValue": "" },
    "list_of_artists": { "ui:emptyValue": "" },
    "list_of_artists_alias": { "ui:emptyValue": "" },
    "insight_number": { "ui:emptyValue": "" },
    "insight_text_note1": { "ui:emptyValue": "" },
    "insight_text_note2": { "ui:emptyValue": "" },
    "insight_text_hyperlink": { "ui:emptyValue": "" },
    "accession_number": { "ui:emptyValue": "" },
    "personality_or_film_or_artwork_on_front_cover": { "ui:emptyValue": "" },
    "personality_or_film_or_artwork_on_front_cover_alias": { "ui:emptyValue": "" },
    "publisher": { "ui:emptyValue": "" },
    "publisher_alias": { "ui:emptyValue": "" },
    "object_alias": { "ui:emptyValue": "" },
    "quotations_and_other_text": { "ui:emptyValue": "" },
    "theme": { "ui:emptyValue": "" },
    "contributors1": { "ui:emptyValue": "" },
    "contributors1_type": { "ui:emptyValue": "" },
    "contributors1_alias": { "ui:emptyValue": "" },
    "contributors2": { "ui:emptyValue": "" },
    "contributors2_type": { "ui:emptyValue": "" },
    "contributors2_alias": { "ui:emptyValue": "" },
    "language_data": { "ui:emptyValue": "" },
    "citation_quotation": { "ui:emptyValue": "" },
    "copyright_of_image": { "ui:emptyValue": "" },
    "age": { "ui:emptyValue": "" }
  };

  // Right column UI schema
  const rightColumnUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "ui:style": {
      fontSize: "14px",
    },
  "is_source": {
    "ui:readonly": true,
    "ui:emptyValue": ""
  },
  "search_text": {
      "ui:readonly": true,
    "ui:options": {
        "addable": false,
      "removable": false,
      "orderable": false
    },
    "ui:emptyValue": ""
  },
  "rec_aliases": {
      "ui:readonly": true,
    "ui:options": {
        "addable": false,
      "removable": false,
      "orderable": false
    },
    "ui:emptyValue": ""
  },
  "extended_tags": {
      "ui:readonly": true,
    "ui:options": {
        "addable": false,
      "removable": false,
      "orderable": false
    },
    "ui:emptyValue": ""
  },
    "set_of": { "ui:emptyValue": "" },
    "copyright": { "ui:emptyValue": "" },
    "facial_recognition_derived_title_of_artwork": { "ui:emptyValue": "" }
  };

  // Combined UI schema for preview mode
  const combinedUiSchema = {
    "ui:submitButtonOptions": { "norender": true },
    "_id": { "ui:widget": "hidden" },
    "ui:style": {
      fontSize: "14px",
    },
    "is_source": {
      "ui:readonly": true,
    "ui:emptyValue": ""
  },
    "search_text": {
      "ui:readonly": true,
      "ui:options": {
        "addable": false,
        "removable": false,
        "orderable": false
      },
    "ui:emptyValue": ""
  },
    "rec_aliases": {
      "ui:readonly": true,
      "ui:options": {
        "addable": false,
        "removable": false,
        "orderable": false
      },
    "ui:emptyValue": ""
  },
  "citation_quotation": {
    "ui:emptyValue": ""
  },
  "copyright_of_image": {
    "ui:emptyValue": ""
  },
  "age": {
    "ui:emptyValue": ""
  },
    "extended_tags": {
      "ui:readonly": true,
      "ui:options": {
        "addable": false,
        "removable": false,
        "orderable": false
      },
    "ui:emptyValue": ""
  },
    "highlight": {
      "ui:readonly": true,
    "ui:emptyValue": ""
  },
    "essential_reading": {
      "ui:emptyValue": '',
    },
    "suggested_reading": {
      "ui:emptyValue": '',
    },
    "agraphy_section_or_sub_section": { "ui:emptyValue": "" },
    "agraphy_specific_section": { "ui:emptyValue": "" },
    "type_of_creator": { "ui:emptyValue": "" },
    "country_or_state_of_artwork_creation": { "ui:emptyValue": "" },
    "location_masterlist": { "ui:emptyValue": "" },
    "list_of_artists": { "ui:emptyValue": "" },
    "list_of_artists_alias": { "ui:emptyValue": "" },
    "insight_number": { "ui:emptyValue": "" },
    "insight_text_note1": { "ui:emptyValue": "" },
    "insight_text_note2": { "ui:emptyValue": "" },
    "insight_text_hyperlink": { "ui:emptyValue": "" },
    "accession_number": { "ui:emptyValue": "" },
    "personality_or_film_or_artwork_on_front_cover": { "ui:emptyValue": "" },
    "personality_or_film_or_artwork_on_front_cover_alias": { "ui:emptyValue": "" },
    "publisher": { "ui:emptyValue": "" },
    "publisher_alias": { "ui:emptyValue": "" },
    "object_alias": { "ui:emptyValue": "" },
    "quotations_and_other_text": { "ui:emptyValue": "" },
    "theme": { "ui:emptyValue": "" },
    "contributors1": { "ui:emptyValue": "" },
    "contributors1_type": { "ui:emptyValue": "" },
    "contributors1_alias": { "ui:emptyValue": "" },
    "contributors2": { "ui:emptyValue": "" },
    "contributors2_type": { "ui:emptyValue": "" },
    "contributors2_alias": { "ui:emptyValue": "" },
    "language_data": { "ui:emptyValue": "" },
    "set_of": { "ui:emptyValue": "" },
    "copyright": { "ui:emptyValue": "" },
    "facial_recognition_derived_title_of_artwork": { "ui:emptyValue": "" }
  };

  // Fetch initial data
  useEffect(() => {
    const getBasicData = async () => {
      try {
        if (alias) {
          const result = await getVideoData(alias, 'section-5');
          if (result) {
            const data = result?.data?.[0] || {};
            
            // Split data for two-column layout
            const { leftData, rightData } = splitFormData(data);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(data);
          }
        } else {
          // For new records, initialize empty forms
          const emptyData = {};
          const { leftData, rightData } = splitFormData(emptyData);
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData(emptyData);
        }
      } catch (error) {
        handleApiError(error, toast, {
          defaultErrorMessage: "Failed to fetch data"
        });
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      let finalFormData;
      
      if (previewEnabled) {
        finalFormData = combinedFormData;
      } else {
        finalFormData = combineFormData(leftFormData, rightFormData);
      }
      
      // Remove _id from create operations
      if (!alias && finalFormData._id) {
        delete finalFormData._id;
      }

      if (alias) {
        const result = await updateVideoData(alias, finalFormData);

        if (result?.status === 200) {
          setQuickSave(prev => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      handleApiError(error, toast, {
        defaultErrorMessage: "Failed to update data"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
              <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                <HStack justifyContent="space-between" mb={4}>
                  <Box fontSize="lg" fontWeight="bold">Videos Quadrant-5 Fields</Box>
                </HStack>
                <Form
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  fontsize={"14px"}
                />
              </Box>
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box display={"flex"} flexDirection={"row"} alignItems={"flex-start"}>
              <Stack flexDirection="row" gap={4} alignItems="flex-start" flex={1}>
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                    <HStack justifyContent="space-between" mb={4}>
                      <Box fontSize="lg" fontWeight="bold">Videos Quadrant-5 Fields</Box>
                    </HStack>
                    <Form
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box flex={1} bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
          <Form 
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema}
                      formData={rightFormData}
        validator={validator}  
                      onChange={handleRightFormChange}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
            </Box>
          )}
        </Box>
      )}
      
      {/* Fixed Save Button */}
      <Box
        position="fixed"
        bottom={0}
        left={"250px"}
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
      >
        <Box maxW="auto" mx="auto" display="flex" justifyContent="center">
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            width="auto"
            margin="0 auto"
            textAlign="center"
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Section5;